/**
 * Who may see the management side of the app.
 *
 * Analytics, Settings and the revenue widgets are for the people who run the
 * agency: the organization's owner and its admins. A `member` uses the
 * day-to-day screens — market explorer, competitor sets, price alerts. Boat
 * owners are not roles at all: they reach one read-only page through a magic
 * link, outside this app's auth entirely.
 *
 * Two rules worth keeping in mind:
 *
 *   - **Super admins pass.** `TenantGuard` resolves a super admin to `owner` on
 *     any organization, so if the UI hid these screens from them, the API would
 *     still serve the data behind them — the gates have to agree.
 *   - **Anything unrecognised is denied.** `organization_members.role` is a
 *     plain varchar with no database constraint, and a role that has not loaded
 *     yet is `undefined`. Defaulting to visible would flash admin nav at a
 *     member on every refresh and then 403 the request behind it.
 *
 * The matching server-side rule is `@OwnerOrAdmin()` in
 * `apps/api/src/organizations/decorators/owner-or-admin.decorator.ts`. Hiding a
 * link is a courtesy; that decorator is what actually protects the data.
 */

const ADMIN_ROLES: readonly string[] = ['owner', 'admin'];

export interface RoleContext {
  /** The viewer's role in the current organization, if it is known yet. */
  role: string | null | undefined;
  isSuperAdmin: boolean;
}

export function canSeeAdminFeatures({ role, isSuperAdmin }: RoleContext): boolean {
  if (isSuperAdmin) return true;
  return role != null && ADMIN_ROLES.includes(role);
}
